/**
 * useGetSceneOriginalEpisodes
 *
 * 시나리오의 원본 에피소드 목록을 무한 스크롤(useInfiniteQuery)로 가져옵니다.
 *
 * ## API 동작
 * - cursor: 에피소드 UUID. 해당 에피소드는 결과에 포함되지 않음.
 * - direction: 'after' → cursor 이후, 'before' → cursor 이전
 * - pagination: hasMore, nextCursor, prevCursor
 *
 * ## 사용처
 * - 일반 모드: cursor 없이 direction='after' → 1번 에피소드부터
 * - Resume 모드(useResumeEpisodes): before/after 각각 별도 쿼리로 사용
 */
import { useInfiniteQuery } from 'react-query';
import {
  getSceneOriginalEpisodes,
  type SceneOriginalEpisodesParams
} from '../../services/sceneService';

export function useGetSceneOriginalEpisodes(
  sceneId: string,
  params?: SceneOriginalEpisodesParams,
  options?: { enabled?: boolean }
) {
  const baseParams = params ?? {};
  const enabled = options?.enabled ?? true;

  return useInfiniteQuery(
    ['sceneOriginalEpisodes', sceneId, baseParams],
    ({ pageParam }) => {
      // pageParam: fetchNextPage/fetchPreviousPage 시 전달되는 값 (getNextPageParam/getPreviousPageParam 반환값)
      const currentCursor = pageParam?.cursor ?? baseParams.cursor;
      const currentDirection = pageParam?.direction ?? baseParams.direction ?? 'after';

      return getSceneOriginalEpisodes({
        sceneId,
        params: {
          ...baseParams,
          cursor: currentCursor,
          direction: currentDirection
        }
      });
    },
    {
      enabled: !!sceneId && enabled,
      staleTime: 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      // fetchNextPage 호출 시 다음 페이지 요청에 사용할 파라미터
      getNextPageParam: (lastPage) => {
        const { hasMore, nextCursor } = lastPage.meta.pagination;
        if (!hasMore || !nextCursor) return undefined;
        return {
          cursor: nextCursor,
          direction: baseParams.direction ?? 'after'
        };
      },
      // fetchPreviousPage 호출 시 이전 페이지 요청에 사용할 파라미터 (항상 before)
      getPreviousPageParam: (firstPage) => {
        const { hasMore, prevCursor } = firstPage.meta.pagination;
        if (!hasMore || !prevCursor) return undefined;
        return { cursor: prevCursor, direction: 'before' as const };
      }
    }
  );
}